import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { PAYROLL_BREAKDOWN } from "@/constants/testIds";

// Destino de la tarjeta "Total de nómina" del Dashboard — desglose de la compensación
// vigente de cada empleado (salario base + componentes), tal como quedó en su último
// registro de la pestaña Salario de su expediente. Al hacer clic en un empleado se abre
// directamente su expediente en esa misma pestaña, con el detalle completo.
export default function PayrollBreakdown() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/payroll-breakdown").then(({ data }) => setData(data));
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-4xl" data-testid={PAYROLL_BREAKDOWN.page}>
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
          <Wallet className="w-7 h-7 text-terracotta" strokeWidth={1.5} /> Nómina
        </h1>
        <p className="text-charcoal/60 mt-1">Compensación mensual vigente de cada empleado activo, con su desglose por concepto.</p>

        {data && (
          <div className="mt-6">
            <p className="text-xs text-charcoal/50">Total mensual</p>
            <p data-testid={PAYROLL_BREAKDOWN.totalPayroll} className="text-2xl font-heading font-bold text-charcoal">
              ${(data.total_payroll ?? 0).toLocaleString("es-MX")}
            </p>
          </div>
        )}

        <div className="space-y-3 mt-6">
          {data?.employees.map((e) => (
            <Link
              key={e.id}
              to={`/admin/empleados/${e.id}?tab=salary`}
              data-testid={PAYROLL_BREAKDOWN.row}
              className="block bg-card border border-petroleum/10 rounded-xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-charcoal">{e.full_name}</p>
                  <p className="text-sm text-charcoal/60">{e.position}</p>
                </div>
                <p className="font-heading font-bold text-charcoal text-lg shrink-0">
                  {e.monthly_salary != null ? `$${e.monthly_salary.toLocaleString("es-MX")}` : "Sin registrar"}
                </p>
              </div>
              {(e.base_salary != null || (Array.isArray(e.components) && e.components.length > 0)) && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {e.base_salary != null && (
                    <span className="text-xs px-2 py-0.5 rounded-full border border-petroleum/20 text-charcoal/70 bg-accent/50">
                      Sueldo base: ${e.base_salary.toLocaleString("es-MX")}
                    </span>
                  )}
                  {Array.isArray(e.components) && e.components.map((comp, i) => (
                    <span
                      key={i}
                      className={`text-xs px-2 py-0.5 rounded-full border ${comp.tag === "fijo_recurrente" ? "border-petroleum/30 text-petroleum bg-petroleum/5" : "border-terracotta/30 text-terracotta bg-terracotta/5"}`}
                    >
                      {comp.name}: {comp.amount != null ? `$${comp.amount.toLocaleString("es-MX")}` : "—"}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
          {data && data.employees.length === 0 && (
            <p data-testid={PAYROLL_BREAKDOWN.emptyState} className="text-charcoal/50 text-sm">Sin empleados activos con compensación registrada.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
